import React from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import ImageDisplay from './../../media/image/ImageDisplay';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import constants from '../../../../qti/Constants';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';

import styles from './dragAndDropInteraction.scss';

const keys = constants.keyCodes;
const validDragEvents = [];
let focusedGapChoice;

class GapChoice extends React.Component {
    static propTypes = {
        backgroundContainerElement: PropTypes.object.isRequired,
        correctResponses: PropTypes.array,
        extraResponse: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
        gapChoiceModel: PropTypes.object.isRequired,
        gapChoicesContainerElement: PropTypes.object,
        handleUserResponse: PropTypes.func.isRequired,
        isCurrent: PropTypes.bool,
        idPrefix: PropTypes.string.isRequired,
        isReadOnly: PropTypes.bool,
        gapChoiceMap: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
        mediaTracker: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
        skipUnansweredIndicator: PropTypes.bool,
        snapHierarchy: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
        userResponse: PropTypes.array,
        wrapperElement: PropTypes.object.isRequired,
        updateStepSize: PropTypes.func,
        stepSize: PropTypes.number,
    };

    static getId(idPrefix, identifier) {
        return `${idPrefix}-GAP_CHOICE-${identifier}`;
    }

    static showImage(gapChoiceModel) {
        const { mediaModel } = gapChoiceModel;
        return mediaModel && mediaModel.type && mediaModel.type.includes('image');
    }

    static showText(gapChoiceModel) {
        return gapChoiceModel.textContent.length > 0;
    }

    static showOutcomeIndicator(props) {
        return props.correctResponses !== undefined;
    }

    constructor(props) {
        const { gapChoiceModel, mediaTracker } = props;
        super(props);
        this.state = {
            focused: false,
            position: this.getDraggableNodePosition(),
        };
        if (GapChoice.showImage(gapChoiceModel)) {
            this.onImageReady = mediaTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier} image`);
        } else {
            this.onReady = mediaTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
        }
        if (GapChoice.showOutcomeIndicator(props)) {
            this.onIconReady = mediaTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier} outcomeIcon`);
        }

        // The following prevents scrolling on drag
        this.scrollable = true;
        this.listener = (e) => {
            if (!this.scrollable) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', this.listener, { passive: false });

        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    componentDidMount() {
        const { gapChoiceModel, isCurrent } = this.props;
        if (isCurrent && gapChoiceModel.identifier === focusedGapChoice) {
            this.rootElement.focus();
        }
        if (!GapChoice.showImage(gapChoiceModel)) {
            this.onReady(true);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const position = this.getDraggableNodePosition(nextProps);
        this.setState({
            position,
        });
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    moveInsideIfOutOfBounds(newX, newY) {
        const draggableArea = this.props.wrapperElement;
        const height = parseInt(draggableArea.clientHeight, 10);
        const width = parseInt(draggableArea.clientWidth, 10);
        const maxX = width - this.rootElement.clientWidth;
        const minX = 0;
        const maxY = height - this.rootElement.clientHeight;
        const minY = 0;
        const newPos = { x: newX, y: newY };

        if (newX < minX) {
            newPos.x = minX;
        } else if (newX > maxX) {
            newPos.x = maxX;
        }

        if (newY < minY) {
            newPos.y = minY;
        } else if (newY > maxY) {
            newPos.y = maxY;
        }

        newPos.wasOutOfBounds = newPos.x !== newX || newPos.y !== newY;
        return newPos;
    }

    getPlaceholderPosition(gapChoiceModel) {
        const elms = this.props.gapChoicesContainerElement.getElementsByClassName(
            `placeholder-${gapChoiceModel.identifier}`
        );
        if (!elms.length > 0) {
            return undefined;
        }

        return { x: elms[0].offsetLeft - 1, y: elms[0].offsetTop - 1 };
    }

    getDraggableNodePosition(props = this.props) {
        const { backgroundContainerElement, gapChoiceMap, gapChoiceModel, idPrefix, snapHierarchy, extraResponse } = props;
        const { geography } = gapChoiceModel;

        if (snapHierarchy && Object.keys(snapHierarchy).length > 0) {
            // Check if the draggable is positioned inside a dropZone.
            const dropZoneId = Object
                .keys(snapHierarchy)
                .find(key => snapHierarchy[key].some(draggable => draggable === gapChoiceModel.identifier));

            if (dropZoneId !== undefined) {
                // The snapIndex of the gapChoice in the dropZone. (Several gapChoices can be in the same dropZone.)
                const indexOfDraggable = snapHierarchy[dropZoneId].indexOf(gapChoiceModel.identifier);

                // Get the dropZone element that the gapChoice is in.
                const dropZone = Array
                    .from(backgroundContainerElement.children)
                    .find(child => child.getAttribute('data-identifier') === dropZoneId);


                let offsetTop = 0;
                for (let i = 0; i < indexOfDraggable; i++) {
                    const draggable = document.getElementById(GapChoice.getId(idPrefix, snapHierarchy[dropZoneId][i]));

                    if (draggable) {
                        offsetTop += draggable.offsetHeight;
                    } else {
                        const tmpGapChoiceIdentifier = snapHierarchy[dropZoneId][i];
                        const tmpGapChoiceModel = gapChoiceMap[tmpGapChoiceIdentifier];
                        if (tmpGapChoiceModel && tmpGapChoiceModel.geography) {
                            offsetTop += tmpGapChoiceModel.geography.height;
                        } else {
                            offsetTop += 30; // A more or less random fallback.
                        }
                    }
                }

                return {
                    x: parseInt(dropZone.style.left, 10),
                    y: parseInt(dropZone.style.top, 10) + offsetTop,
                };
            }
        }

        // Check if the gapChoice is free placed.
        if (extraResponse
            && extraResponse[gapChoiceModel.identifier]
            && extraResponse[gapChoiceModel.identifier].length > 0) {
            const positionArray = extraResponse[gapChoiceModel.identifier][0].split(' ');
            return {
                y: parseInt(positionArray[1], 10),
                x: parseInt(positionArray[0], 10),
            };
        }

        // Check if the gapChoice has an initial position on the background image.
        if (geography && geography.top !== undefined) {
            return {
                x: parseInt(geography.left, 10),
                y: parseInt(geography.top, 10),
            };
        }

        // Use the position of the corresponding placeholder image in the gapChoicesContainerElement.
        return this.getPlaceholderPosition(gapChoiceModel);
    }

    getDraggableNodeDimensions() {
        const { geography } = this.props.gapChoiceModel;

        return {
            height: `${geography.height}px`,
            width: `${geography.width}px`,
        };
    }

    getAnswerOption() {
        const { gapChoiceModel } = this.props;
        let answerOption = '';

        for (let i = 0; i < this.props.userResponse.length; i++) {
            const userReponses = this.props.userResponse[i].split(' ');
            if (userReponses[0] === gapChoiceModel.identifier) {
                answerOption = this.props.userResponse[i];
            }
        }

        return answerOption;
    }

    getIsAttempted() {
        const { gapChoiceModel } = this.props;
        let isAttempted = false;

        for (let i = 0; i < this.props.userResponse.length; i++) {
            const idx = this.props.userResponse[i].indexOf(gapChoiceModel.identifier);
            if (idx > -1) {
                isAttempted = true;
                break;
            }
        }

        return isAttempted;
    }

    handleBlur = () => {
        this.setState({ focused: false });
    };

    handleArrowKeyUp = () => {
        const { gapChoicesContainerElement, handleUserResponse } = this.props;
        const { x, y } = this.state.position;

        if (gapChoicesContainerElement && y > gapChoicesContainerElement.offsetTop) {
            handleUserResponse(this.rootElement, undefined, undefined);
        } else {
            handleUserResponse(this.rootElement, x, y);
        }

        return false;
    };

    handleKeyDown = (e) => {
        if (!this.state.focused) {
            return true;
        }

        if (e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            e.preventDefault();
            return this.handleArrowKeys(e.keyCode, e.shiftKey);
        }

        return true;
    };

    handleKeyUp = (e) => {
        const { updateStepSize } = this.props;
        if (!this.state.focused) {
            return true;
        }

        const newStepSize = constants.keyCodeStepSizeMap[e.keyCode];
        if (newStepSize && typeof updateStepSize === 'function') {
            updateStepSize(newStepSize);
        }

        if (e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            e.preventDefault();
            return this.handleArrowKeyUp();
        }

        return true;
    };


    handleDragStart = (event) => {
        validDragEvents.push(event);
    };

    handleDrag = () => {
        this.scrollable = false;
    };

    /**
     * @param {Event} event
     * @param {DraggableData} draggableData
     */
    handleDragEnd = (event, draggableData) => {
        const { gapChoicesContainerElement, handleUserResponse } = this.props;
        const draggedElement = draggableData.node;
        const lastX = parseInt(draggableData.lastX, 10);
        const lastY = parseInt(draggableData.lastY, 10);
        const position = { x: lastX, y: lastY };

        this.setState({ position });
        this.scrollable = true;

        if (gapChoicesContainerElement && position.y > gapChoicesContainerElement.offsetTop) {
            // In the gapChoicesContainerElement
            handleUserResponse(draggedElement, undefined, undefined);
        } else {
            // In the canvas
            handleUserResponse(draggedElement, position.x, position.y);
        }
    };

    handleArrowKeys = (keyCode, shiftKeyPressed) => {
        const { position = { x: 0, y: 0 } } = this.state;
        const selectedStepSize = this.props.stepSize ? this.props.stepSize : 1;
        const stepSize = shiftKeyPressed ? 10 : selectedStepSize;
        let x = position.x;
        let y = position.y;

        if (keyCode === keys.DOWN) {
            y += stepSize;
        } else if (keyCode === keys.LEFT) {
            x -= stepSize;
        } else if (keyCode === keys.RIGHT) {
            x += stepSize;
        } else if (keyCode === keys.UP) {
            y -= stepSize;
        }

        const fixedPos = this.moveInsideIfOutOfBounds(x, y);
        this.setState({ position: { x: fixedPos.x, y: fixedPos.y } });
        return false;
    };

    handleOnFocus = () => {
        const { gapChoiceModel } = this.props;
        focusedGapChoice = gapChoiceModel.identifier;
        this.setState({ focused: true });
    };

    render() {
        const { correctResponses, gapChoiceModel, idPrefix, isReadOnly, skipUnansweredIndicator } = this.props;
        const { backgroundContainerElement, wrapperElement } = this.props;
        const { identifier, mediaModel } = gapChoiceModel;
        const { position } = this.state;
        const hasACorrectTarget = correctResponses && correctResponses.find(response => response.split(' ')[0] === gapChoiceModel.identifier);
        const answerOption = this.getAnswerOption();
        const isAttempted = this.getIsAttempted();
        const tabIndex = isReadOnly ? undefined : '0';
        const style = gapChoiceModel.type === 'gapImg' ? this.getDraggableNodeDimensions() : undefined;
        const handle = isReadOnly ? undefined : '.draggable';
        const cancel = isReadOnly ? '.draggable' : undefined;
        const onFocus = isReadOnly ? undefined : this.handleOnFocus;
        const a11yAttributes = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v2) ? {
            'role': 'button',
            'aria-roledescription': t('ariaRoleDescription.draggableItem'),
        } : {};

        return (
            <Draggable
                bounds={`#${wrapperElement.id}`}
                cancel={cancel}
                onStart={this.handleDragStart}
                onDrag={this.handleDrag}
                onStop={this.handleDragEnd}
                handle={handle}
                position={position}
                offsetParent={backgroundContainerElement}
            >
                <div
                    data-identifier={gapChoiceModel.identifier}
                    data-type="draggable"
                    className={`${styles.gapChoice} ${gapChoiceModel.type} draggable`}
                    id={GapChoice.getId(idPrefix, identifier)}
                    onFocus={onFocus}
                    onBlur={this.handleBlur}
                    ref={(element) => { this.rootElement = element; }}
                    style={style}
                    tabIndex={tabIndex}
                    {...a11yAttributes}
                >
                    {
                        GapChoice.showImage(gapChoiceModel) ?
                            <ImageDisplay model={mediaModel} reportReady={this.onImageReady} autoScale />
                            : null
                    }
                    {
                        GapChoice.showText(gapChoiceModel) ?
                            <div className={styles.textNode} dangerouslySetInnerHTML={{ __html: gapChoiceModel.textContent }} />
                            : null
                    }
                    {
                        GapChoice.showOutcomeIndicator(this.props) ?
                            <InteractionOutcomeIndicator
                                answerOption={answerOption}
                                correctResponses={correctResponses}
                                isAttempted={isAttempted}
                                onReady={this.onIconReady}
                                skipUnansweredIndicator={!hasACorrectTarget || skipUnansweredIndicator}
                            />
                            : null
                    }
                </div>
            </Draggable>
        );
    }
}

export default GapChoice;
